/**
 * SessionCard — fila de día dentro de WeekView (Sprint 6 Bloque 4,
 * pulido Sprint 8 S8.2 / UX-006).
 *
 * Render compacto de una `TrainingSession`: día (Lun/Mar/...), tipo
 * humanizado, target principal (distancia o duración), badge de estado
 * con color + icono. Click → detalle de la sesión.
 *
 * Estados visuales (5; uno es derivado, ver `utils/sessionVisualState`):
 * - planned: neutral, sin icono — sesión futura.
 * - completed: verde con ✓.
 * - partial: amarillo con ◐.
 * - skipped: rojo discreto con ⏭.
 * - overdue (DERIVADO): amarillo intenso con ⚠ — planned + fecha pasada.
 *   Indica al atleta "olvidaste reportar/saltar".
 */
import { useState } from "react";
import { Link } from "react-router-dom";

import { exportSessionFit, patchSession } from "@/api/training";
import { EffortSelector } from "@/components/training/EffortSelector";

import { t } from "@/i18n/es";
import { labelForVamTestKind } from "@/lib/vamTestKindLabel";
import {
  VISUAL_STATE_BADGE_CLASSES,
  VISUAL_STATE_I18N_KEY,
  VISUAL_STATE_ICONS,
  getSessionVisualState,
} from "@/utils/sessionVisualState";
import type { SessionType, TrainingSession } from "@/types/api";

const SESSION_TYPE_LABELS: Record<SessionType, string> = {
  rest: "sessionTypeRest",
  z2: "sessionTypeZ2",
  z2_fartlek: "sessionTypeZ2Fartlek",
  caco_z2: "sessionTypeCacoZ2",
  caco_plus: "sessionTypeCacoPlus",
  series_cortas: "sessionTypeSeriesCortas",
  series_medias: "sessionTypeSeriesMedias",
  tempo_continuo: "sessionTypeTempoContinuo",
  larga_progresiva: "sessionTypeLargaProgresiva",
  rodaje_z3: "sessionTypeRodajeZ3",
  tempo_largo: "sessionTypeTempoLargo",
  taper_combo: "sessionTypeTaperCombo",
  cuestas_v15: "sessionTypeCuestasV15",
  test_vam: "sessionTypeTestVam",
  rod_z2_corto: "sessionTypeRodZ2Corto",
  long_run_trail: "sessionTypeLongRunTrail",
  rodaje_previo: "sessionTypeRodajePrevio",
  activacion_pre_carrera: "sessionTypeActivacionPreCarrera",
};

const DAY_SHORT_KEYS = [
  "dayShortMon",
  "dayShortTue",
  "dayShortWed",
  "dayShortThu",
  "dayShortFri",
  "dayShortSat",
  "dayShortSun",
] as const;

function _getDayLabel(isoDate: string): string {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "—";
  // JS getDay(): domingo=0, lunes=1 ... — convertimos a lunes=0 (Nexum).
  const jsDay = d.getDay();
  const nexumIdx = jsDay === 0 ? 6 : jsDay - 1;
  return t.plan[DAY_SHORT_KEYS[nexumIdx]];
}

function _formatTarget(session: TrainingSession): string | null {
  if (session.session_type === "rest") return null;
  if (session.distance_target_km) {
    return `${session.distance_target_km} km`;
  }
  if (session.duration_target_min) {
    return `${session.duration_target_min} min`;
  }
  return null;
}

export interface SessionCardProps {
  session: TrainingSession;
  /** ISO YYYY-MM-DD del día actual. Solo para tests — en producción
   *  no se pasa y se calcula con `new Date()` en `getSessionVisualState`.
   *  Permite verificar estado `overdue` con fechas deterministas. */
  today?: string;
  /** Sprint 10 S10.2b: flag local inyectado por `WeekView` cuando
   *  detecta que esta sesión es la sustituta `rod_z2_corto` planned
   *  creada tras un reporte de fatiga en mismo `session_date`. NO se
   *  serializa desde el backend (se deriva client-side, ver
   *  `_detectFatigueSubstituteIds` en WeekView). */
  isFatigueSubstitute?: boolean;
  /** v14 §3: gate de RENDER del ritmo /km. `session.pace_range` ya viene
   *  gated por VAM desde el backend; encima, el ritmo SOLO se muestra si
   *  el atleta tiene reloj. Default undefined (reloj desconocido / no
   *  pasado, p.ej. PreVamGateView) → no se muestra ritmo. */
  hasWatch?: boolean;
  /** Sesión 33 EXTENDIDA — Alba feature (Conversación 2 Alberto).
   *  Si true, el card muestra "Sesión N" en lugar del día (LUN/MAR/...).
   *
   *  Sub-bloque 5 (observación Yan): aplica a TODAS las sesiones,
   *  incluyendo rest. La numeración respeta el orden cronológico del
   *  plan completo (sesión 1 a 7 por semana). Mantiene la estructura
   *  visual del plan — solo cambia el header del día por el ordinal.
   *
   *  `sessionIndex` 1-indexed dentro de la semana (incluye rest).
   *  Backend NO cambia `session_date` — solo cambia render. */
  flexibleScheduling?: boolean;
  sessionIndex?: number;
  /** F3 (sesión 51, v16.5/v16.6): exportación FIT a reloj. Lo calcula
   *  PlanPage desde el perfil (espejo de los gates del backend):
   *  - "enabled": amateur + reloj + VAM medida → botón activo.
   *  - "needs_vam": amateur + reloj SIN VAM → botón desactivado con
   *    aviso "Completa el Test VAM…".
   *  - undefined: sin reloj / no amateur → no se muestra nada.
   *  Solo aplica a sesiones PENDIENTES (planned, no overdue) no-rest. */
  fitExport?: "enabled" | "needs_vam";
  /** s53.2 (E.1): callback tras marcar la sesión como hecha desde la
   *  card — PlanPage pasa su `refresh` para que la card se actualice
   *  sin recargar la página. Si no llega, el botón Marcar no aparece
   *  (contextos sin plumbing, p.ej. tests antiguos). */
  onSessionUpdated?: () => void;
}

export function SessionCard({
  session,
  today,
  isFatigueSubstitute,
  hasWatch,
  flexibleScheduling,
  sessionIndex,
  fitExport,
  onSessionUpdated,
}: SessionCardProps) {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  // s55 Fix A / s57: ayuda colapsable de importación. La ruta web de
  // Garmin publicada en s55 no existe — aviso provisional hasta que
  // Alberto valide el método USB (T-smoke v3).
  const [fitHelpOpen, setFitHelpOpen] = useState(false);
  const [markOpen, setMarkOpen] = useState(false);
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState(false);
  const dayLabel = _getDayLabel(session.session_date);
  // Sesión 33 EXTENDIDA sub-bloque 5 (observación Yan): en modo flexible,
  // "Sesión N" sustituye al día para TODAS las sesiones (incluyendo rest).
  // La numeración respeta el orden plan completo (1-7 por semana) — la
  // calcula WeekView en orden cronológico. Si flexibleScheduling=true
  // pero sessionIndex no llega (prop-drilling roto), fallback al día.
  const showFlexLabel = (
    flexibleScheduling === true
    && typeof sessionIndex === "number"
  );
  const headerLabel = showFlexLabel
    ? t.plan.sessionCardFlexibleLabel.replace("{n}", String(sessionIndex))
    : dayLabel;
  const typeLabel = t.plan[SESSION_TYPE_LABELS[session.session_type] as keyof typeof t.plan];
  const target = _formatTarget(session);
  // Sprint 8 S8.2: estado visual incluye `overdue` derivado (planned +
  // fecha pasada). El badge muestra icono Unicode + label i18n.
  const visualState = getSessionVisualState(session, today);
  const statusLabel = t.plan[
    VISUAL_STATE_I18N_KEY[visualState] as keyof typeof t.plan
  ];
  const statusIcon = VISUAL_STATE_ICONS[visualState];
  const isRest = session.session_type === "rest";
  const isTest = session.session_type === "test_vam";
  // Sprint 10 S10.2b: badges contextuales para flow fatiga.
  // - "Saltada por fatiga": original skipped con skip_reason='fatigue'.
  //   Mismo estilo discreto que el badge de skipped genérico pero copy
  //   específico para el atleta.
  // - "Sustituta por fatiga": sub planned creada por el motor en mismo
  //   día (flag client-side desde WeekView).
  const isSkippedByFatigue =
    session.completion_status === "skipped"
    && session.skip_reason === "fatigue";

  // F3: pendiente = planned y NO vencida (overdue no se exporta).
  const isPending =
    session.completion_status === "planned" && visualState !== "overdue";
  const showFitExport = fitExport !== undefined && isPending && !isRest;

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    setExportError(false);
    try {
      const { blob, filename } = await exportSessionFit(session.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  };

  // s53.2 (E.1): "Marcar hecha" — ESPEJO del gate del detalle ("Acciones
  // sólo si planned", SIN restricción de fecha: incluye vencidas, que es
  // el caso principal de adherencia). rest no se marca.
  const showMark =
    onSessionUpdated !== undefined
    && session.completion_status === "planned"
    && !isRest;

  const completeSession = async (effort: number | null) => {
    if (marking) return;
    setMarking(true);
    setMarkError(false);
    try {
      await patchSession(session.id, {
        action: "complete_manual",
        ...(effort !== null ? { perceived_effort: effort } : {}),
      });
      setMarkOpen(false);
      onSessionUpdated?.();
    } catch {
      setMarkError(true);
    } finally {
      setMarking(false);
    }
  };

  const handleMarkClick = () => {
    // ESPEJO v16.3 §2 (gate POR SESIÓN del detalle): si la sesión ya
    // tiene actividad Strava vinculada, los datos vienen del import →
    // completar directo SIN modal. Si no, mini-modal de esfuerzo.
    if (session.actual_activity_id != null) {
      void completeSession(null);
      return;
    }
    setMarkError(false);
    setMarkOpen(true);
  };

  const card = (
    <Link
      to={`/plan/sesion/${session.id}`}
      data-testid={`session-card-${session.id}`}
      data-status={session.completion_status}
      data-visual-state={visualState}
      data-session-type={session.session_type}
      className={`flex items-center justify-between rounded-md border px-4 py-3 text-bone transition-colors duration-fast hover:border-border-strong ${
        isTest
          ? "border-lime/40 bg-lime/5"
          : "border-border-subtle bg-carbon-raised"
      }`}
    >
      <div className="flex items-center gap-4">
        <span
          className="w-16 text-xs font-medium uppercase tracking-widest text-gray-mid"
          aria-label={headerLabel}
          data-testid={`session-day-${session.id}`}
        >
          {headerLabel}
        </span>
        <div className="flex flex-col">
          <span className="flex items-center gap-2">
            <span className={`text-sm ${isRest ? "text-bone-muted" : "text-bone"}`}>
              {typeLabel}
              {/* v16.1 §8 sub-bloque 3: sufijo distinguible solo para
                  test_vam. El helper devuelve "" si no hay kind o no
                  es test_vam, por lo que el concatenado es seguro. */}
              {isTest && labelForVamTestKind(session.vam_test_kind)}
            </span>
            {isTest && (
              <span
                data-testid={`session-test-badge-${session.id}`}
                className="rounded-full border border-lime/50 bg-lime/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-lime"
              >
                {t.plan.testBadgeLabel}
              </span>
            )}
            {isSkippedByFatigue && (
              <span
                data-testid={`session-fatigue-skipped-badge-${session.id}`}
                className="rounded-full border border-danger/50 bg-danger/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest text-danger"
              >
                {t.plan.sessionBadgeSkippedByFatigue}
              </span>
            )}
            {isFatigueSubstitute && (
              <span
                data-testid={`session-fatigue-substitute-badge-${session.id}`}
                className="rounded-full border border-warning/50 bg-warning/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest text-warning"
              >
                {t.plan.sessionBadgeFatigueSubstitute}
              </span>
            )}
          </span>
          {target && (
            <span className="text-xs text-bone-muted">
              {target}
              {session.intensity_zone && session.intensity_zone !== "mixed"
                ? ` · ${session.intensity_zone}`
                : ""}
              {/* v14 §3: ritmo /km tras la zona. Gate de 2 capas: VAM
                  (ya aplicado en backend → pace_range null si no hay) +
                  has_watch (aquí). Sin reloj o sin ritmo → nada (sin
                  guiones huérfanos). El string ya viene formateado. */}
              {session.pace_range && hasWatch
                ? ` · ${session.pace_range}`
                : ""}
            </span>
          )}
        </div>
      </div>
      {!isRest && (
        <span
          data-testid={`session-status-badge-${session.id}`}
          className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${
            VISUAL_STATE_BADGE_CLASSES[visualState]
          }`}
        >
          {statusIcon && (
            <span aria-hidden="true" className="text-[10px] leading-none">
              {statusIcon}
            </span>
          )}
          <span>{statusLabel}</span>
        </span>
      )}
    </Link>
  );

  // F3: el botón vive FUERA del <Link> (la card entera navega al
  // detalle; anidar un interactivo dentro rompería a11y y dispararía
  // la navegación al pulsar). Wrapper transparente para el layout.
  return (
    <div>
      {card}
      {isTest && isPending && (
        <p
          data-testid={`test-vam-encouragement-${session.id}`}
          className="mt-1 px-2 text-xs text-bone-muted"
        >
          {t.plan.testVamEncouragement}
        </p>
      )}
      {(showFitExport || showMark) && (
        <div className="mt-1 flex items-center gap-2 px-2">
          {showMark && (
            <button
              type="button"
              data-testid={`mark-session-button-${session.id}`}
              disabled={marking}
              onClick={handleMarkClick}
              className="rounded-md border border-lime/50 px-2 py-0.5 text-xs text-lime disabled:cursor-not-allowed disabled:opacity-50"
            >
              {marking ? t.plan.markSessionSaving : t.plan.markSessionButton}
            </button>
          )}
          {showMark && markError && (
            <span
              data-testid={`mark-session-error-${session.id}`}
              className="text-xs text-red-400"
            >
              {t.plan.markSessionError}
            </span>
          )}
          {showFitExport && (
          <button
            type="button"
            data-testid={`fit-export-button-${session.id}`}
            disabled={fitExport === "needs_vam" || exporting}
            onClick={handleExport}
            className="rounded-md border border-bone-muted/40 px-2 py-0.5 text-xs text-bone disabled:cursor-not-allowed disabled:opacity-50"
          >
            {exporting
              ? t.plan.fitExportDownloading
              : t.plan.fitExportButton}
          </button>
          )}
          {showFitExport && fitExport === "needs_vam" && (
            <span
              data-testid={`fit-export-needs-vam-${session.id}`}
              className="text-xs text-bone-muted"
            >
              {t.plan.fitExportNeedsVam}
            </span>
          )}
          {showFitExport && exportError && (
            <span
              data-testid={`fit-export-error-${session.id}`}
              className="text-xs text-red-400"
            >
              {t.plan.fitExportError}
            </span>
          )}
          {showFitExport && fitExport === "enabled" && (
            <button
              type="button"
              data-testid={`fit-help-toggle-${session.id}`}
              onClick={() => setFitHelpOpen((v) => !v)}
              className="text-xs text-bone-muted underline decoration-dotted"
            >
              {t.plan.fitHelpToggle}
            </button>
          )}
        </div>
      )}
      {showFitExport && fitHelpOpen && (
        <div
          data-testid={`fit-help-panel-${session.id}`}
          className="mt-2 rounded-md border border-border-subtle bg-black/20 p-2 text-xs text-bone-muted"
        >
          <p>{t.plan.fitHelpNotice}</p>
        </div>
      )}
      {markOpen && (
        // Mini-modal de esfuerzo (s53.2): overlay clicable fuera =
        // cancelar SIN marcar. Un toque en el selector → PATCH → cierra.
        // SIN botón omitir (decisión producto Yan s53).
        <div
          data-testid={`mark-session-modal-${session.id}`}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setMarkOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t.plan.markSessionTitle}
            className="w-full max-w-xs rounded-xl border border-border-subtle bg-graphite p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="mb-3 text-sm font-medium text-bone">
              {t.plan.markSessionTitle}
            </p>
            <EffortSelector
              value={null}
              onChange={(n) => {
                if (n !== null) void completeSession(n);
              }}
              label={t.plan.postSessionEffortLabel}
              testIdPrefix={`mark-effort-${session.id}`}
            />
            {markError && (
              <p className="mt-2 text-xs text-red-400">
                {t.plan.markSessionError}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
